import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Pencil, FileDown } from 'lucide-react';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import apiClient, { get, post, patch } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import { formatMoney, formatDate, today, daysAgo, primero, useLocalTable } from './shared.js';
import FiltroFechas from './FiltroFechas.jsx';
import { RESOURCES_FINANZAS_CXP } from '../../config/resources.finanzas-cxp.js';
import { buildTablePdf, exportTableExcel, cargarLogosPdf } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

/**
 * Recibo PDF individual de un Pago -- replica `PDFOC()` de `ListadoPagos.jsx` (V1):
 * un botón por fila que descarga un comprobante de una sola tabla (fecha,
 * proveedor, forma de pago, importe). Como V1, lleva SOLO el logo Sistemify
 * centrado arriba (67,15,70,20), sin logo de institución.
 */
async function imprimirReciboPago(row) {
  const doc = new jsPDF();
  const { sistemify } = await cargarLogosPdf();
  try {
    if (sistemify) doc.addImage(sistemify, 'PNG', 67, 15, 70, 20);
  } catch (e) {
    console.warn('No se pudo agregar el logo al recibo', e);
  }
  doc.setFontSize(16);
  doc.text('___________________', 77, 80);
  doc.text('Firma', 98, 90);
  autoTable(doc, {
    head: [['Fecha', 'Proveedor', 'Forma Pago', 'Importe']],
    body: [[row.fecha, row.proveedor, row.formaPago, formatMoney(row.importe)]],
    startY: 45,
  });
  doc.save('Abono.pdf');
}

const cfg = RESOURCES_FINANZAS_CXP.pagos;

/**
 * Modal de edición de Pago — replica `PagosEdit.jsx` de V1: permite reasignar
 * proveedor, banco (con su moneda), tipo de cambio e importe. El backend
 * (`pagos.service.js#editar`) reproduce 1:1 la conversión a MXN de
 * `PagosController.js#edit` cuando el banco (original o nuevo) es USD.
 */
function EditarPagoModal({ open, onClose, pago, proveedores, bancos, onEditado }) {
  const [fecha, setFecha] = useState('');
  const [proveedor, setProveedor] = useState('');
  const [formaPago, setFormaPago] = useState('Efectivo');
  const [banco, setBanco] = useState('');
  const [tipoCambio, setTipoCambio] = useState(1);
  const [importe, setImporte] = useState(0);
  const [importeUSD, setImporteUSD] = useState(0);
  const [concepto, setConcepto] = useState('Abono');
  const [folio, setFolio] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open || !pago) return;
    setFecha((pago.fecha || '').slice(0, 10));
    setProveedor(pago._idProveedor || '');
    setFormaPago(pago.formaPago || 'Efectivo');
    setBanco(pago._idBanco || '');
    setTipoCambio(pago.tipoCambio || 1);
    setImporte(pago.importe || 0);
    setImporteUSD(Number(((pago.importe || 0) / (pago.tipoCambio || 1)).toFixed(2)));
    setConcepto('Abono');
    setFolio(pago.folio || '');
    setObservaciones(pago.observaciones || '');
  }, [open, pago]);

  function jalaTipoCambio(valor) {
    const tc = parseFloat(valor) || 1;
    setTipoCambio(tc);
    setImporte(parseFloat((importeUSD * tc).toFixed(2)));
  }

  function cambiaImporte(valor) {
    const nuevo = Number(valor) || 0;
    setImporte(valor);
    setImporteUSD(parseFloat((nuevo / (Number(tipoCambio) || 1)).toFixed(2)));
  }

  async function guardar(e) {
    e.preventDefault();
    if (!banco) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'Debe seleccionar un Banco' }); return; }
    const r = await Swal.fire({
      title: '¿Estas seguro?', text: 'Se actualizará el pago', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Actualizar!',
    });
    if (!r.isConfirmed) return;

    setEnviando(true);
    try {
      await patch(`/${cfg.route}/${pago._id}`, {
        fecha, importe: Number(importe), proveedores: proveedor, formaPago,
        observaciones, bancos: banco, tipoCambio: Number(tipoCambio), folio,
      });
      Swal.fire('Good job!', 'Actualizado con éxito', 'success');
      onEditado();
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar', text: err.message });
    } finally {
      setEnviando(false);
    }
  }

  if (!pago) return null;
  const bancoSel = bancos.find((b) => b._id === banco);

  return (
    <Modal open={open} onClose={onClose} title="Editar Pago" width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cerrar</button>
          <button type="submit" form="form-editar-pago" disabled={enviando} className="btn-primary">
            {enviando ? 'Guardando...' : 'Actualizar'}
          </button>
        </>
      )}
    >
      <form id="form-editar-pago" onSubmit={guardar} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Fecha</label>
          <input type="date" required value={fecha} onChange={(e) => setFecha(e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Proveedor</label>
          <select required value={proveedor} onChange={(e) => setProveedor(e.target.value)}
            className="select-base">
            <option value="">Selecciona</option>
            {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Concepto</label>
          <select value={concepto} onChange={(e) => setConcepto(e.target.value)} className="select-base">
            <option value="">Selecciona un Concepto</option>
            <option value="Abono">Abono</option>
            <option value="Bonificacion">Bonificacion</option>
          </select>
        </div>
        <div>
          <label className="label">Forma de Pago</label>
          <select value={formaPago} onChange={(e) => setFormaPago(e.target.value)}
            className="select-base">
            <option value="">Selecciona una Forma de Pago</option>
            <option value="Efectivo">Efectivo</option>
            <option value="Transfer">Transfer</option>
            <option value="Bonificacion">Bonificacion</option>
          </select>
        </div>
        <div>
          <label className="label">Banco</label>
          <select required value={banco} onChange={(e) => setBanco(e.target.value)}
            className="select-base">
            <option value="">Selecciona un Banco</option>
            {[...bancos].sort((a, b) => (a.banco > b.banco ? 1 : -1)).map((b) => (
              <option key={b._id} value={b._id}>{b.banco} {b.cuenta} ({b.moneda})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Tipo de Cambio</label>
          <input type="number" min="0.01" step="0.01" value={tipoCambio} onChange={(e) => jalaTipoCambio(e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Folio</label>
          <input type="text" required value={folio} onChange={(e) => setFolio(e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Importe ({bancoSel?.moneda || 'USD'})</label>
          <input type="number" min="0" step="any" value={importe} onChange={(e) => cambiaImporte(e.target.value)}
            className="input-base" />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Observaciones</label>
          <input type="text" value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
            className="input-base" />
        </div>
      </form>
    </Modal>
  );
}

export default function PagosPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso(`${cfg.recurso}:ver`);
  const puedeCrear = hasPermiso(`${cfg.recurso}:crear`);
  const puedeEditar = hasPermiso(`${cfg.recurso}:editar`);
  const [pagoEditando, setPagoEditando] = useState(null);

  // --- Catálogos ---
  const [proveedores, setProveedores] = useState([]);
  const [bancos, setBancos] = useState([]);

  // --- Listado de pagos ya registrados ---
  const [pagos, setPagos] = useState([]);
  const [cargandoListado, setCargandoListado] = useState(true);
  const [error, setError] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(daysAgo(30));
  const [fechaFin, setFechaFin] = useState(today());
  const [mailOpen, setMailOpen] = useState(false);
  const [filtroProveedor, setFiltroProveedor] = useState('');
  const [filtroFormaPago, setFiltroFormaPago] = useState('');

  // --- Formulario de captura ---
  const [fecha, setFecha] = useState(today());
  const [selectedProveedor, setSelectedProveedor] = useState('');
  const [concepto, setConcepto] = useState('Abono');
  const [formaPago, setFormaPago] = useState('Efectivo');
  const [selectedBanco, setSelectedBanco] = useState('');
  const [folio, setFolio] = useState('');
  const [tipoCambio, setTipoCambio] = useState(1);
  const [lineas, setLineas] = useState([]);
  const [enviando, setEnviando] = useState(false);

  const cargarCatalogos = useCallback(async () => {
    const [provData, bancosData] = await Promise.all([
      apiClient.get('/proveedores', { params: { limit: 500 } }).then((r) => r.data.data),
      apiClient.get('/bancos', { params: { limit: 500 } }).then((r) => r.data.data),
    ]);
    setProveedores(provData || []);
    setBancos(bancosData || []);
  }, []);

  const cargarPagos = useCallback(async () => {
    setCargandoListado(true);
    setError(null);
    try {
      const data = await get(`/${cfg.route}/fechas/${fechaInicio}/${fechaFin}`);
      setPagos((data || []).map((p) => {
        const prov = primero(p.proveedores);
        const banco = primero(p.bancos);
        return {
          _id: p._id,
          numero: p.idPago,
          fecha: p.fecha,
          folio: p.folio,
          proveedor: prov?.name || '—',
          _idProveedor: prov?._id,
          _idBanco: banco?._id,
          formaPago: p.formaPago || '',
          observaciones: p.observaciones || '',
          importe: p.importe,
          tipoCambio: p.tipoCambio,
          is_active: p.is_active,
        };
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargandoListado(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { cargarCatalogos(); }, [cargarCatalogos]);
  useEffect(() => { cargarPagos(); }, [cargarPagos]);

  const cargarPendientesProveedor = useCallback(async (proveedorId) => {
    if (!proveedorId) { setLineas([]); return; }
    const [gc, g, cv] = await Promise.all([
      get(`/gastos-contenedor/proveedor/${proveedorId}/por-pagar`),
      get(`/gastos/proveedor/${proveedorId}/por-pagar`),
      get(`/comisiones-vendedor/proveedor/${proveedorId}`).catch(() => []),
    ]);
    const lineasGC = (gc || []).map((a) => ({
      id: a._id, tipo: 'gastosContenedor', fecha: a.fecha, nota: String(a.idGastoContenedor),
      totalUSD: a.totalUSD, tipoCambio: a.tipoCambio, total_general: a.total, abonos: a.abonos,
      saldo: a.saldo, importe: a.saldo, observaciones: 'NA', select: false,
    }));
    const lineasG = (g || []).map((a) => ({
      id: a._id, tipo: 'gastos', fecha: a.fecha, nota: String(a.idGasto),
      totalUSD: a.totalUSD, tipoCambio: a.tipoCambio, total_general: a.total, abonos: a.abonos,
      saldo: a.saldo, importe: a.saldo, observaciones: 'NA', select: false,
    }));
    const lineasCV = (cv || []).filter((a) => a.saldo > 0).map((a) => ({
      id: a._id, tipo: 'comisionesVendedor', fecha: a.fecha,
      nota: `Comisión Vendedor - Anticipo ${primero(a.anticipo)?.idAnticipo ?? 'N/A'}`,
      totalUSD: a.importe, tipoCambio: 1, total_general: a.importe, abonos: a.abonos,
      saldo: a.saldo, importe: a.saldo, observaciones: 'Comisión Vendedor', select: false,
    }));
    setLineas([...lineasGC, ...lineasG, ...lineasCV]);
  }, []);

  useEffect(() => { cargarPendientesProveedor(selectedProveedor); }, [selectedProveedor, cargarPendientesProveedor]);

  function actualizaImporte(id, valor) {
    setLineas((prev) => prev.map((l) => {
      if (l.id !== id) return l;
      const num = Number(valor) || 0;
      if (num > l.saldo) {
        Swal.fire({ icon: 'error', title: 'Oops...', text: 'El Pago excede el Saldo' });
        return l;
      }
      return { ...l, importe: num };
    }));
  }

  function actualizaObservaciones(id, valor) {
    setLineas((prev) => prev.map((l) => (l.id === id ? { ...l, observaciones: valor } : l)));
  }

  function toggleSelect(id, checked) {
    setLineas((prev) => prev.map((l) => (l.id === id ? { ...l, select: checked } : l)));
  }

  function jalaTipoCambio(valor) {
    const tc = Number(valor) || 0;
    setTipoCambio(valor);
    setLineas((prev) => prev.map((l) => {
      const totalGeneral = l.totalUSD * tc;
      const saldo = totalGeneral - l.abonos;
      return { ...l, tipoCambio: tc, total_general: totalGeneral, saldo, importe: saldo };
    }));
  }

  const total = lineas.filter((l) => l.select).reduce((acc, l) => acc + (Number(l.importe) || 0), 0);

  async function registrarPago(e) {
    e.preventDefault();
    if (!selectedProveedor) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'Debe seleccionar un proveedor' }); return; }
    if (!selectedBanco) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'Debe seleccionar un Banco' }); return; }
    if (!folio) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'Debe ingresar un Folio' }); return; }
    const seleccionadas = lineas.filter((l) => l.select && Number(l.importe) > 0);

    const r = await Swal.fire({
      title: 'Estas seguro?', text: 'Se registrará el abono', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Enviar!',
    });
    if (!r.isConfirmed) return;

    setEnviando(true);
    try {
      for (const linea of seleccionadas) {
        const payload = {
          fecha, importe: Number(linea.importe), proveedores: selectedProveedor,
          formaPago, observaciones: linea.observaciones, bancos: selectedBanco,
          tipoCambio: linea.tipoCambio, folio,
        };
        payload[linea.tipo] = linea.id;
        await post(`/${cfg.route}`, payload);
      }
      Swal.fire('Good job!', 'Creado con exito', 'success');
      setSelectedProveedor(''); setFolio(''); setLineas([]); setTipoCambio(1);
      await cargarPagos();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: err.message });
    } finally {
      setEnviando(false);
    }
  }

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(
    pagos.filter((p) => (!filtroProveedor || p._idProveedor === filtroProveedor) && (!filtroFormaPago || p.formaPago === filtroFormaPago)),
    {
      searchFields: ['proveedor', 'formaPago', 'observaciones', 'importe', 'numero'],
      defaultSortBy: 'numero', defaultSortOrder: 'desc', perPage: 50,
    },
  );

  // Columnas EXACTAS del PDF/Excel de V1 (`ListadoPagos.jsx` -> `PDFTabla()`/`excel()`).
  const columnasPdf = [
    { key: 'numero', label: 'Numero' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'proveedor', label: 'Proveedor' },
    { key: 'formaPago', label: 'Forma Pago' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'importe', label: 'Importe', render: (r) => formatMoney(r.importe) },
  ];
  const columnasExcel = [
    { key: 'numero', label: 'Numero' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'proveedor', label: 'Proveedor' },
    { key: 'formaPago', label: 'FormaPago' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'importe', label: 'Importe' },
  ];
  const totalExport = filtradas.reduce((a, r) => a + (r.importe || 0), 0);
  const armarPdf = () => buildTablePdf('Listado Abonos', columnasPdf, filtradas, { subtitulo: `${fechaInicio} - ${fechaFin}`, totalCols: { observaciones: 'Total', importe: `$${new Intl.NumberFormat('en-US').format(totalExport)}` } });

  const columnas = [
    { key: 'numero', label: 'Número', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'folio', label: 'Folio', sortable: true },
    { key: 'proveedor', label: 'Proveedores', sortable: true },
    { key: 'formaPago', label: 'Forma Pago', sortable: true },
    { key: 'observaciones', label: 'Observaciones', sortable: true },
    { key: 'importe', label: 'Importe', sortable: true, render: (r) => formatMoney(r.importe) },
    {
      key: '__pdf', label: 'PDF', sortable: false,
      render: (row) => (
        <button
          title="PDF" aria-label="PDF"
          className="p-1.5 rounded-md text-danger-500 hover:bg-slate-100"
          onClick={() => imprimirReciboPago(row)}
        >
          <FileDown size={15} />
        </button>
      ),
    },
    {
      key: '__acciones', label: 'Editar', sortable: false,
      render: (row) => (
        <button
          title="Editar" aria-label="Editar" disabled={!puedeEditar}
          className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50 disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={() => setPagoEditando(row)}
        >
          <Pencil size={15} />
        </button>
      ),
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">{cfg.title}</h1>

      {puedeCrear && (
        <form onSubmit={registrarPago} className="card p-4 space-y-4">
          <h2 className="font-medium text-slate-800 text-center">Captura un Pago</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <label className="label">Fecha</label>
              <input type="date" required value={fecha} onChange={(e) => setFecha(e.target.value)}
                className="input-base" />
            </div>
            <div>
              <label className="label">Proveedor</label>
              <select required value={selectedProveedor} onChange={(e) => setSelectedProveedor(e.target.value)}
                className="select-base">
                <option value="">Selecciona</option>
                {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => (
                  <option key={p._id} value={p._id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Concepto</label>
              <select value={concepto} onChange={(e) => setConcepto(e.target.value)} className="select-base">
                <option value="">Selecciona un Concepto</option>
                <option value="Abono">Abono</option>
                <option value="Bonificacion">Bonificacion</option>
              </select>
            </div>
            <div>
              <label className="label">Forma de Pago</label>
              <select value={formaPago} onChange={(e) => setFormaPago(e.target.value)}
                className="select-base">
                <option value="">Selecciona una Forma de Pago</option>
                <option value="Efectivo">Efectivo</option>
                <option value="Transfer">Transfer</option>
                <option value="Bonificacion">Bonificacion</option>
              </select>
            </div>
            <div>
              <label className="label">Banco</label>
              <select value={selectedBanco} onChange={(e) => setSelectedBanco(e.target.value)}
                className="select-base">
                <option value="">Selecciona un Banco</option>
                {[...bancos].filter((b) => b.moneda === 'MXN').sort((a, b) => (a.banco > b.banco ? 1 : -1)).map((b) => (
                  <option key={b._id} value={b._id}>{b.banco} {b.cuenta}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end justify-end gap-2">
              <span className="text-lg font-semibold text-slate-800">Total Pago</span>
              <span className="rounded-full bg-danger-600 text-white px-3 py-1 text-sm font-semibold">{formatMoney(total)}</span>
            </div>
            <div>
              <label className="label">Tipo de Cambio</label>
              <input type="number" value={tipoCambio} disabled={!selectedProveedor}
                onChange={(e) => jalaTipoCambio(e.target.value)}
                className="input-base" />
            </div>
            <div>
              <label className="label">Folio</label>
              <input type="text" required value={folio} onChange={(e) => setFolio(e.target.value)}
                className="input-base" />
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-2 py-2"></th>
                  <th className="px-2 py-2 text-left">Fecha</th>
                  <th className="px-2 py-2 text-left">Nota</th>
                  <th className="px-2 py-2 text-left">Total USD</th>
                  <th className="px-2 py-2 text-left">TC</th>
                  <th className="px-2 py-2 text-left">Total MXN</th>
                  <th className="px-2 py-2 text-left">Abonos</th>
                  <th className="px-2 py-2 text-left">Saldo</th>
                  <th className="px-2 py-2 text-left">Importe</th>
                  <th className="px-2 py-2 text-left">Observaciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lineas.length === 0 && (
                  <tr><td colSpan={10} className="px-3 py-4 text-center text-slate-400">
                    {selectedProveedor ? 'Sin partidas pendientes' : 'Selecciona un proveedor'}
                  </td></tr>
                )}
                {lineas.map((l) => (
                  <tr key={l.id}>
                    <td className="px-2 py-2">
                      <input type="checkbox" checked={l.select} onChange={(e) => toggleSelect(l.id, e.target.checked)} />
                    </td>
                    <td className="px-2 py-2">{formatDate(l.fecha)}</td>
                    <td className="px-2 py-2">{l.nota}</td>
                    <td className="px-2 py-2">{formatMoney(l.totalUSD)}</td>
                    <td className="px-2 py-2">{formatMoney(l.tipoCambio)}</td>
                    <td className="px-2 py-2">{formatMoney(l.total_general)}</td>
                    <td className="px-2 py-2">{formatMoney(l.abonos)}</td>
                    <td className="px-2 py-2">{formatMoney(l.saldo)}</td>
                    <td className="px-2 py-2">
                      <input type="number" min="0" step="any" disabled={!l.select} value={l.importe}
                        onChange={(e) => actualizaImporte(l.id, e.target.value)}
                        className="input-base w-28" />
                    </td>
                    <td className="px-2 py-2">
                      <input type="text" value={l.observaciones} onChange={(e) => actualizaObservaciones(l.id, e.target.value)}
                        className="input-base" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button type="submit" disabled={enviando} className="btn-primary">
            Registrar
          </button>
        </form>
      )}

      {enviando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-white rounded-xl p-10 text-center max-w-md">
            <h3 className="text-2xl font-semibold text-slate-800">Procesando</h3>
            <p className="mt-4 text-slate-500">Por favor no cierre ni refresque su navegador.</p>
            <p className="mt-4 text-sm italic text-slate-400">Este proceso puede tomar unos instantes...</p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <h2 className="text-center text-xl font-semibold text-slate-900">Reporte de Pagos</h2>
        <FiltroFechas inicio={fechaInicio} fin={fechaFin} onInicio={setFechaInicio} onFin={setFechaFin} />
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">Proveedores</label>
            <select value={filtroProveedor} onChange={(e) => { setFiltroProveedor(e.target.value); setPage(1); }} className="select-base">
              <option value="">Selecciona</option>
              {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => (
                <option key={p._id} value={p._id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Forma Pago</label>
            <select value={filtroFormaPago} onChange={(e) => { setFiltroFormaPago(e.target.value); setPage(1); }} className="select-base">
              <option value="">Selecciona una Forma de Pago</option>
              <option value="Efectivo">Efectivo</option>
              <option value="Transfer">Transfer</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..."
            className="input-base w-64" />
          <button type="button" onClick={async () => (await armarPdf()).save('Abonos.pdf')}
            className="btn-outline text-xs px-3 py-1.5">PDF</button>
          <button type="button" onClick={() => setMailOpen(true)}
            className="btn-outline text-xs px-3 py-1.5">eMail</button>
          <button type="button" onClick={() => exportTableExcel('ListadoPagos', columnasExcel, filtradas, { fileName: 'ListadoPagos', sheetName: 'ListadoPagos' })}
            className="btn-outline text-xs px-3 py-1.5">Excel</button>
          {error && <span className="text-sm text-danger-600">{error}</span>}
        </div>
        <DataTable columns={columnas} data={pageRows} loading={cargandoListado} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
          footer={{ observaciones: 'TOTAL', importe: formatMoney(pageRows.reduce((a, r) => a + (r.importe || 0), 0)) }} />
        <Pagination pagination={pagination} page={page} onChange={setPage} />
      </div>

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Abonos"
        onEnviar={async (email) => {
          const doc = await armarPdf();
          await enviarCorreoAdjunto({
            subject: 'Listado Abonos', email, fileName: 'ListadoAbonos.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras el listado de Abonos.'),
          });
        }}
      />

      <EditarPagoModal
        open={!!pagoEditando}
        onClose={() => setPagoEditando(null)}
        pago={pagoEditando}
        proveedores={proveedores}
        bancos={bancos}
        onEditado={cargarPagos}
      />
    </div>
  );
}
